import {
  Droplet,
  Activity,
  HeartPulse,
  Waves,
  Stethoscope,
  ShieldCheck,
  ScanLine,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/lib/site";

const ICONS = { Droplet, Activity, HeartPulse, Waves, Stethoscope, ShieldCheck, ScanLine };

// Soft, on-brand gradient header per card — consistent, premium, and free of
// stock photography that could feel generic or non-local.
const GRADIENTS = [
  "from-[#0369A1] to-[#0EA5E9]",
  "from-[#BE185D] to-[#F472B6]",
  "from-[#0D9488] to-[#2DD4BF]",
  "from-[#7C3AED] to-[#A78BFA]",
  "from-[#0369A1] to-[#38BDF8]",
  "from-[#C2410C] to-[#FB923C]",
  "from-[#1E3A8A] to-[#3B82F6]",
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#0369A1]">
              Our Services
            </span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B2545]">
              Advanced ultrasound &amp; imaging, under one roof.
            </h2>
          </div>
          <p className="md:text-right text-[#64748B] max-w-md">
            From pregnancy and Doppler studies to thyroid, breast and pelvic scans — every
            study is reported by senior radiologists, often the same day.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] ?? Activity;
            return (
              <a
                key={s.slug}
                href="#appointment"
                data-testid={`service-${s.slug}`}
                className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col"
              >
                <div
                  className={`relative h-28 bg-gradient-to-br ${GRADIENTS[idx % GRADIENTS.length]} flex items-center px-6`}
                >
                  <Icon className="w-12 h-12 text-white/95" strokeWidth={1.7} />
                  <span className="absolute top-3 right-4 text-[11px] font-bold tracking-[0.22em] text-white/80">
                    0{idx + 1}
                  </span>
                  <div className="absolute inset-0 opacity-[0.12] [background-image:radial-gradient(circle_at_80%_20%,white_1px,transparent_1px)] [background-size:14px_14px]" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-xl font-bold text-[#0B2545]">{s.name}</h3>
                  <p className="mt-2 text-sm text-[#64748B] leading-relaxed flex-1">{s.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0369A1]">
                    Book this scan
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
